import React, { useEffect, useState } from "react";
import {
  Layout,
  Button,
  Row,
  Col,
  Pagination,
  List,
  Card,
  Tag,
  Checkbox,
  Input,
  message,
  Tooltip,
} from "antd";
import {
  FileTextOutlined,
  LinkOutlined,
  FilePdfOutlined,
  ProfileFilled,
  FileExcelOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import "./content.scss";
import TopHeader from "../../../components/header/Header";
import TrainAndContentPopup from "../DialogBox/TrainAndContentPopup";
import EditRolesModal from "../DialogBox/EditRolesModal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  deleteContents,
  getAllUploadedDocuments,
  setSelectedTrainStatus,
} from "../../../redux/slices/trainAndContentSlice";
import {
  setPage,
  setConfirmationContent,
  setConfirmationData,
  setConfirmationTitle,
  setIsConfirmation,
  setIsMultipleRecordsSelected,
} from "../../../redux/slices/globalSlice";
import Confirmation from "../../../components/dialog/Confirmation";
import {
  isTrainAndContentAdd,
  isTrainAndContentEdit,
  isTrainAndContentNotification,
  isTrainAndContentDelete,
} from "../../../utils/permissions";
import Loading from "../../../components/Loader/Loading";
import Notification from "../firebaseNotification/Notification";
import { registerServiceWorker } from "../firebaseNotification/main";

const { Content } = Layout;

export const TrainAndContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isTrainContentPopup, setIsTrainContentPopup] = useState(false);
  const [editRolesContent, setEditRolesContent] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagesByType, setPagesByType] = useState({
    text: 1,
    url: 1,
    pdf: 1,
    csv: 1,
  });
  const { page, limit, searchInput, totalPage, contentLoader } = useSelector(
    (state) => state.globalReducer
  );
  const { selectedTrainStatus, allDocument, loading } = useSelector(
    (state) => state.trainAndContentReducer
  );
  const trainContentOpen = () => {
    setIsTrainContentPopup(true);
  };

  const getStoredPage = (type) => pagesByType[type] || 1;
  const setPageForType = (type, nextPage) => {
    setPagesByType((prev) => {
      if (prev[type] === nextPage) {
        return prev;
      }
      return { ...prev, [type]: nextPage };
    });
    dispatch(setPage(nextPage));
  };
  const getDocuments = (type, pageOverride) => {
    const targetPage = pageOverride ?? getStoredPage(type);
    dispatch(
      getAllUploadedDocuments({
        page: targetPage,
        limit: limit,
        type: type,
        navigate: navigate,
        searchQuery: searchInput || "",
      })
    ).then((action) => {
      const { payload } = action;
      if (payload && payload.data && payload.data.length > 0) {
        const firstDocument = payload.data[0];
        setSelectedItem(firstDocument);
      } else {
        setSelectedItem(null);
      }
    });
  };

  useEffect(() => {
    const initialPage = getStoredPage(selectedTrainStatus);
    dispatch(setPage(initialPage));
    getDocuments(selectedTrainStatus, initialPage);
  }, []);

  const handleContentTypeChange = (type) => {
    dispatch(setSelectedTrainStatus(type));
    const storedPage = getStoredPage(type);
    dispatch(setPage(storedPage));
    getDocuments(type, storedPage);
  };

  const multiDeleteConfirmationOpen = () => {
    if (!selectedRowKeys.length) return;

    const plural = selectedRowKeys.length > 1 ? "s" : "";
    const title = `Delete Content`;
    const content = `Are you sure you want to delete ${selectedRowKeys.length} item${plural}`;

    dispatch(setConfirmationTitle(title));
    dispatch(setConfirmationContent(content));
    dispatch(setConfirmationData(selectedRowKeys));
    dispatch(setIsConfirmation(true));
    dispatch(setIsMultipleRecordsSelected(true));
  };

  const handleRemoveContent = () => {
    dispatch(
      deleteContents({
        fileIds: selectedRowKeys,
        page: page,
        limit: limit,
        fileType: selectedItem?.type,
        navigate: navigate,
        searchQuery: searchInput || "",
      })
    )
      .unwrap()
      .then((response) => {
        if (response?.results && Array.isArray(response.results)) {
          let hasSuccess = false;
          let hasError = false;
          const errorMessages = [];
          response.results.forEach((result) => {
            if (result.success) {
              hasSuccess = true;
            } else {
              hasError = true;
              let errorMsg = result.error || "Failed to delete";
              if (result.details) {
                const detailsStr = Array.isArray(result.details)
                  ? result.details.join(", ")
                  : String(result.details);
                errorMsg = errorMsg;
              }
              errorMessages.push(errorMsg);
            }
          });
          if (hasSuccess && !hasError) {
            message.success("Record(s) deleted successfully");
          } else if (hasError && !hasSuccess) {
            errorMessages.forEach((msg) => message.error(msg));
          } else {
            message.warning("Some records were deleted, but some failed");
            errorMessages.forEach((msg) => message.error(msg));
          }
        } else {
          message.success("Record(s) deleted successfully");
        }
        setSelectedItem(null);
        setSelectedRowKeys([]);
        // closeConfirmation();
      })
      .catch((error) => {
        // closeConfirmation();
      });
    closeConfirmation();
  };

  const getPdfTooltipContent = (doc) => {
    const normalizeValue = (value) =>
      typeof value === "string" ? value.trim() : value;

    // Capitalize the first letter of the string
    const capitalizeFirst = (str) =>
      typeof str === "string" && str.length > 0
        ? str.charAt(0).toUpperCase() + str.slice(1)
        : str;
    const toTitleCase = (str) =>
      typeof str === "string"
        ? str
            .trim()
            .split(/\s+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        : str;
    // Category should be UPPERCASE
    const categoryValue = (() => {
      const val = normalizeValue(doc?.category || doc?.documentType);
      return typeof val === "string" ? val.toUpperCase() : val;
    })();
    const childCompanyValue = toTitleCase(
      normalizeValue(doc?.childCompanyName)
    );

    const countryValue = capitalizeFirst(normalizeValue(doc?.country));
    const regionValue = capitalizeFirst(normalizeValue(doc?.region));

    const detailFields = [
      categoryValue && { label: "Category", value: categoryValue },
      childCompanyValue && { label: "Child Company", value: childCompanyValue },
      countryValue && { label: "Country", value: countryValue },
      regionValue && { label: "Region", value: regionValue },
    ].filter(Boolean);

    if (!detailFields.length) {
      return <span>No additional details available</span>;
    }

    return (
      <div className="pdf-tooltip-content">
        {detailFields.map(({ label, value }) => (
          <div key={label}>
            <span className="pdf-tooltip-label">{label}:</span> {value}
          </div>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const closeConfirmation = () => {
    dispatch(setConfirmationTitle(""));
    dispatch(setConfirmationContent(""));
    dispatch(setConfirmationData(null));
    dispatch(setIsConfirmation(false));
    dispatch(setIsMultipleRecordsSelected(false));
  };

  const onSelectCheckboxItem = (id, e) => {
    if (e.target.checked) {
      setSelectedRowKeys([...selectedRowKeys, id]);
    } else {
      setSelectedRowKeys(selectedRowKeys.filter((item) => item !== id));
    }
  };

  const isDeleteDisabled = () => {
    return !selectedRowKeys.some(Boolean);
  };

  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <Layout className="custom-layout">
      <TopHeader
        isTitle={true}
        isRightContent={false}
        title="Train and Content"
        icon={<ProfileFilled className="icon-size text-white" />}
      />
      <Content className="content-panel">
        <div className="train-content">
          <div className="train-left">
            <Row gutter={12}>
              <Col span={10}>
                {isTrainAndContentAdd && (
                  <Button block onClick={trainContentOpen}>
                    <PlusOutlined />
                    Add Training Data
                  </Button>
                )}
              </Col>

              <Col span={10}>
                <Input.Search
                  placeholder="Search documents"
                  allowClear
                  size="middle"
                  onSearch={(value) => {
                    const trimmedValue = value.trim();
                    setPageForType(selectedTrainStatus, 1);
                    dispatch(
                      getAllUploadedDocuments({
                        page: 1,
                        limit,
                        type: selectedTrainStatus,
                        navigate,
                        searchQuery: trimmedValue,
                      })
                    );
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value.trim()) {
                      setPageForType(selectedTrainStatus, 1);
                      dispatch(
                        getAllUploadedDocuments({
                          page: 1,
                          limit,
                          type: selectedTrainStatus,
                          navigate,
                          searchQuery: "",
                        })
                      );
                    }
                  }}
                />
              </Col>
              <Col span={4}>
                {isTrainAndContentDelete && (
                  <Button
                    block
                    onClick={multiDeleteConfirmationOpen}
                    disabled={loading || isDeleteDisabled()}
                    danger
                  >
                    <DeleteOutlined />
                  </Button>
                )}
              </Col>
            </Row>
            <div className="train-form">
              <Button
                size="large"
                type={selectedTrainStatus === "text" ? "primary" : "default"}
                icon={<FileTextOutlined />}
                onClick={() => {
                  handleContentTypeChange("text");
                }}
                className={`content-type-btn ${
                  selectedTrainStatus === "text"
                    ? "selected-btn"
                    : "inactive-btn"
                }`}
              >
                Text
              </Button>
              <Button
                size="large"
                type={selectedTrainStatus === "url" ? "primary" : "default"}
                icon={<LinkOutlined />}
                onClick={() => {
                  handleContentTypeChange("url");
                }}
                className={`content-type-btn ${
                  selectedTrainStatus === "url"
                    ? "selected-btn"
                    : "inactive-btn"
                }`}
              >
                URL
              </Button>
              <Button
                size="large"
                type={selectedTrainStatus === "pdf" ? "primary" : "default"}
                icon={<FilePdfOutlined />}
                onClick={() => {
                  handleContentTypeChange("pdf");
                }}
                className={`content-type-btn ${
                  selectedTrainStatus === "pdf"
                    ? "selected-btn"
                    : "inactive-btn"
                }`}
              >
                PDF
              </Button>
              <Button
                size="large"
                type={selectedTrainStatus === "csv" ? "primary" : "default"}
                icon={<FileExcelOutlined />}
                onClick={() => {
                  handleContentTypeChange("csv");
                }}
                className={`content-type-btn ${
                  selectedTrainStatus === "csv"
                    ? "selected-btn"
                    : "inactive-btn"
                }`}
              >
                CSV
              </Button>
            </div>
            <div className="train-items">
              {loading ? (
                <Loading />
              ) : allDocument?.data?.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "#888",
                    fontSize: "16px",
                  }}
                >
                  No Data Available
                </div>
              ) : (
                <div className="train-items-scrollable">
                  {allDocument?.data?.map((list) => {
                    return (
                      <List
                        key={list._id}
                        onClick={() => setSelectedItem(list)}
                      >
                        <Col
                          className={`train-item ${
                            list._id === selectedItem?._id
                              ? "train-item-selected"
                              : ""
                          }`}
                        >
                          <Row>
                            <Col span={isTrainAndContentEdit || isTrainAndContentDelete ? 16 : 24} className="train-item-name">
                              {list.fileName.replace(/\.(pdf|txt|doc)$/, "") ||
                                list.fileName}
                            </Col>
                            <Col span={isTrainAndContentEdit || isTrainAndContentDelete ? 8 : 0} className="train-item-btn">
                              {isTrainAndContentEdit && (
                                <Tooltip title="Edit roles" placement="top">
                                  <EditOutlined
                                    className="train-edit-icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditRolesContent(list);
                                    }}
                                  />
                                </Tooltip>
                              )}
                              {isTrainAndContentDelete && (
                                <>
                                  {list?.type === "pdf" && (
                                    <Tooltip
                                      title={getPdfTooltipContent(list)}
                                      placement="top"
                                    >
                                      <InfoCircleOutlined className="pdf-info-icon" />
                                    </Tooltip>
                                  )}
                                  <Checkbox
                                    checked={selectedRowKeys.includes(list._id)}
                                    onChange={(e) =>
                                      onSelectCheckboxItem(list._id, e)
                                    }
                                  />
                                </>
                              )}
                            </Col>
                          </Row>
                          {list.role?.length > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: 8,
                              }}
                            >
                              {" "}
                              <Row style={{ marginTop: 10 }}>
                                {list.role.map((roleName) => (
                                  <Tag>{roleName}</Tag>
                                ))}
                              </Row>
                              <div style={{ fontSize: 12, color: "#888" }}>
                                {formatDate(list?.updatedAt) ||
                                  formatDate(list?.createdAt)}
                              </div>
                            </div>
                          )}
                        </Col>
                      </List>
                    );
                  })}
                </div>
              )}
              <div className="pagination-footer-content text-center">
                <Pagination
                  current={page}
                  pageSize={limit}
                  total={totalPage}
                  onChange={(pageNumber, pageSize) => {
                    setPageForType(selectedTrainStatus, pageNumber);
                    dispatch(
                      getAllUploadedDocuments({
                        page: pageNumber,
                        limit: pageSize,
                        type: selectedTrainStatus,
                        navigate: navigate,
                        searchQuery: searchInput || "",
                      })
                    );
                  }}
                  showSizeChanger={false}
                  className="pagination-content"
                />
              </div>
            </div>
          </div>
          <div className="train-right">
            <div className="preview-title">Preview</div>

            <div className="preview-content-panel">
              {contentLoader ? (
                <Loading />
              ) : !selectedItem || allDocument?.data?.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "#888",
                  }}
                >
                  No Preview Available
                </div>
              ) : (
                <div className="preview-content">
                  {selectedItem?.type === "url" ? (
                    <Card>
                      <Tag
                        type="primary"
                        shape="round"
                        icon={<LinkOutlined />}
                        size={"large"}
                        color="blue"
                        className="url-link"
                        onClick={() =>
                          window.open(selectedItem?.urlLink, "_blank")
                        }
                        style={{
                          cursor: "pointer",
                          whiteSpace: "normal",
                          wordBreak: "break-all",
                          overflowWrap: "anywhere",
                          display: "inline-block",
                          maxWidth: "100%",
                        }}
                      >
                        {selectedItem?.urlLink}
                      </Tag>
                    </Card>
                  ) : selectedItem?.type === "csv" ? (
                    <Card>
                      <Tag
                        type="primary"
                        shape="round"
                        icon={<FileExcelOutlined />}
                        size={"large"}
                        color="green"
                        className="csv-link"
                        onClick={() =>
                          window.open(selectedItem?.location, "_blank")
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {selectedItem?.fileName} (Click to view CSV)
                      </Tag>
                      <div
                        style={{ marginTop: 8, fontSize: 12, color: "#666" }}
                      >
                        File URL: {selectedItem?.location}
                      </div>
                    </Card>
                  ) : (
                    <>
                      {loading ? (
                        <Loading />
                      ) : (
                        <iframe
                          className="preview-inner"
                          title="Preview"
                          src={selectedItem?.location}
                        ></iframe>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Content>
      <TrainAndContentPopup
        isOpen={isTrainContentPopup}
        setIsOpen={setIsTrainContentPopup}
        selectedType={selectedTrainStatus}
      />
      <EditRolesModal
        visible={!!editRolesContent}
        onClose={() => setEditRolesContent(null)}
        content={editRolesContent}
        page={page}
        limit={limit}
        fileType={selectedTrainStatus}
        searchQuery={searchInput || ""}
      />
      <Confirmation
        isRemove={true}
        onCancel={closeConfirmation}
        onClose={closeConfirmation}
        onRemoveMultiple={handleRemoveContent}
      />
      {isTrainAndContentNotification && <Notification />}
    </Layout>
  );
};
